import {
	AGG_TYPES,
	BOX_SHADOWS,
	CHART_TYPES,
	FONTS,
	PRIMARY_COLOR,
} from "../../../constants";

import { createStyles, makeStyles, Theme } from "@material-ui/core";
import { max } from "mathjs";
import { Chart } from "react-google-charts";
import { BAR_COLORS, getBarColors } from "../../VariableCorrelations/DualChart";
import { ChartHeader } from "../ChartHeader";
import { chartInputsType } from "./DataTable";
import { Legend } from "./Legend";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		chartContainer: {
			width: "90%",
			marginLeft: "5%",
		},
		chartContainer2: {
			width: "100%",
			display: "flex",
			flexDirection: "column",
			position: "relative",
		},
		container: {
			display: "flex",
			flexDirection: "column",
			width: "100%",
			backgroundColor: "white",
			boxShadow: BOX_SHADOWS,
			borderRadius: "10px",
		},
	})
);

export function ChartXTitle({ xName }) {
	return (
		<div
			style={{
				color: "rgba(0,0,0,0.6)",
				fontFamily: FONTS,
				fontSize: "14px",
				zIndex: 1000,
				marginTop: "400px",
				marginBottom: "-400px",
				width: "100%",
				marginLeft: "20px",
				height: "0px",
				textAlign: "center",
			}}
		>
			{xName}
		</div>
	);
}

export function ChartYTitle({ yName }) {
	let mrg = 170 + yName.length * 4 + "px";
	return (
		<div
			style={{
				color: "rgba(0,0,0,0.6)",
				fontFamily: FONTS,
				fontSize: "14px",
				zIndex: 100,
				transform: "rotate(-90deg)",
				height: "30px",
				width: "0px",
				marginTop: mrg,
				backgroundColor: "blue",
				marginRight: "20px",
				marginLeft: "-20px",
				whiteSpace: "nowrap",
			}}
		>
			{yName}
		</div>
	);
}

export function GroupedColumn({
	chartProps,
	chartAggData,
	state,
	isPreview,
}: chartInputsType) {
	const classes = useStyles();
	const header: any[] = [""];
	let data = [];

	const isPercentage = [
		AGG_TYPES.percentage_col,
		AGG_TYPES.percentage_row,
		AGG_TYPES.percentage_total,
	].includes(chartProps.agg_type);
	const div_val = isPercentage ? 100 : 1;

	let bar_colors = BAR_COLORS;
	const agg = chartAggData;
	const isSingleChart = chartProps.var1 === chartProps.var2;
	const isLineChart = chartProps.chart_type === CHART_TYPES.line_chart;

	if (isSingleChart) {
		header.push("");
		header.push({ role: "tooltip" });
		data = [header];
		for (let i = 0; i < agg.arr.length; i++) {
			data.push([
				agg.rowLabels[i],
				Number.parseFloat(
					agg.arr[i][i]
						.replace(",", "")
						.replace("-", isLineChart ? "-" : "0")
				) / div_val,
				chartProps.var2 +
					": " +
					agg.colLabels[i] +
					"\n" +
					agg.arr[i][i],
			]);
		}
		bar_colors = [PRIMARY_COLOR];
	} else {
		const nClassesY = chartAggData.colLabels.length;

		for (let i = 0; i < nClassesY; i++) {
			header.push(chartAggData.colLabels[i]);
			header.push({ role: "style" });
			header.push({ role: "tooltip" });
		}

		data = [header];

		let colors = getBarColors(Array(agg.arr[0].length).fill(""));

		let colName = chartProps.var2;
		for (let i = 0; i < agg.arr.length; i++) {
			let subRow = agg.arr[i].map((val, j) => [
				Number.parseFloat(
					agg.arr[i][j]
						.replace(",", "")
						.replace("-", isLineChart ? "-" : "0")
				) / div_val,
				isLineChart
					? `stroke-opacity: ${colors.alphas[j]}; stroke-color: ${colors.colors[j]}; stroke-width: 2;`
					: `fill-color: ${colors.colors[j]}; fill-opacity: ${colors.alphas[j]}; stroke-color: #76A7FA; stroke-width: 0;`,
				colName + ": " + agg.colLabels[j] + "\n" + agg.arr[i][j],
			]);
			var merged = [].concat.apply([], subRow);
			let row = [agg.rowLabels[i], ...merged];
			data.push(row);
		}
	}

	// Calc Left Spacing Needed
	let leftSpacing = 0;

	const isBarChart = [
		CHART_TYPES.grouped_bar,
		CHART_TYPES.stacked_bar,
	].includes(chartProps.chart_type);
	const isAreaChart = chartProps.chart_type === CHART_TYPES.area_chart;

	const needsLeftSpacing = [
		CHART_TYPES.grouped_bar,
		CHART_TYPES.stacked_bar,
	].includes(chartProps.chart_type);

	if (needsLeftSpacing) {
		leftSpacing = max(chartAggData.rowLabels.map((v) => v.length)) * 10;
		leftSpacing = Math.min(leftSpacing, 200);
		leftSpacing = Math.max(leftSpacing, 20);
	}
	const needsBottomSpacing = [
		CHART_TYPES.line_chart,
		CHART_TYPES.area_chart,
		CHART_TYPES.grouped_col,
		CHART_TYPES.stacked_col,
	].includes(chartProps.chart_type);
	let bottomSpacing = 0;
	if (needsBottomSpacing) {
		bottomSpacing = max(chartAggData.rowLabels.map((v) => v.length)) * 5;
		bottomSpacing = Math.min(bottomSpacing, 60);
		bottomSpacing = Math.max(bottomSpacing, 20);
	}

	let yTitle = `${chartProps.agg_type} of ${chartProps.value}`;
	let xTitle = chartProps.var1;
	if (isBarChart) {
		let tmp = yTitle;
		yTitle = xTitle;
		xTitle = tmp;
	}

	return (
		<div className={classes.chartContainer}>
			<div style={{ width: "109%", marginLeft: "-5%" }}>
				<ChartHeader
					chartAggData={chartAggData}
					state={state}
					chartProps={chartProps}
					isPreview={isPreview}
				/>
			</div>
			<div style={{ display: "flex", flexDirection: "row" }}>
				<ChartYTitle yName={yTitle}></ChartYTitle>
				<div className={classes.chartContainer2}>
					<ChartXTitle xName={xTitle}></ChartXTitle>
					<div
						style={{
							height: "100%",
							width: "100%",
							marginLeft: "0%",
						}}
					>
						<Chart
							style={{
								height: "400px",
								width: "100%",
								zIndex: 1000,
							}}
							chartType={
								isBarChart
									? "BarChart"
									: isAreaChart
									? "AreaChart"
									: isLineChart
									? "LineChart"
									: "ColumnChart"
							}
							loader={<div>Loading Chart</div>}
							data={data}
							options={{
								pointSize: 3,
								colors: bar_colors,
								legend: { position: "none" },
								chartArea: {
									width: "100%",
									height: "100%",
									top: 10,
									bottom: needsBottomSpacing
										? bottomSpacing
										: 30,
									left: needsLeftSpacing ? leftSpacing : 40,
								},
								isStacked: [
									CHART_TYPES.stacked_col,
									CHART_TYPES.stacked_bar,
									CHART_TYPES.area_chart,
								].includes(chartProps.chart_type),

								vAxis: {
									minorGridlines: { color: "white" },
									gridlines: { color: "rgb(220,220,220)" },
									baselineColor: "rgb(220,220,220)",
									format: isPercentage ? "#%" : "#",
								},
								hAxis: {
									minorGridlines: { color: "white" },
									baselineColor: "rgb(220,220,220)",
									gridlines: { color: "rgb(220,220,220)" },
									format: isPercentage ? "#%" : "#",
								},

								annotations: {
									alwaysOutside: true,
									textStyle: {
										color: PRIMARY_COLOR,
										fontSize: 14,
										fontFamily: FONTS,
									},
								},
							}}
						/>
					</div>
				</div>
			</div>
			<div style={{ marginTop: "10px", height: "40px" }}>
				{isSingleChart ? (
					<div></div>
				) : (
					<Legend
						yCol={state.saveState.dataset.getColumn(
							chartProps.var2
						)}
						legendNames={chartAggData.colLabels}
					></Legend>
				)}
			</div>
		</div>
	);
}
export function GroupedColumnWithContainer({
	chartProps,
	chartAggData,
	state,
	isPreview,
}: chartInputsType) {
	const classes = useStyles();
	return (
		<div className={classes.container}>
			<GroupedColumn
				state={state}
				chartProps={chartProps}
				chartAggData={chartAggData}
				isPreview={false}
			></GroupedColumn>
		</div>
	);
}
